export * from './block';
export * from './buildpc';
export * from './cart';
export * from './combo';
export * from './common';
export * from './confirm18YearsOld';
export * from './datetime';
export * from './discovery';
export * from './env';
export * from './hamper';
export * from './layout';
export * from './menuItem';
export * from './mockStorage';
export * from './navigation-bar';
export * from './products';
export * from './related-promotions';
export * from './service-provider';
export * from './smartBanner';
export * from './teko-oauth';

import { PageTypeEnum } from '@teko/discovery';
import { logger } from '@teko/log';
import slug from '@teko/slug';
import { USER_LOCATION_CODE, USER_TERMINAL_ID } from '../constants';
import { ExtraPromotion } from '../types';
import { getCookieByName, getEnv } from './env';

export const transformExtraPromotion = (
  promotion: ExtraPromotion,
): ExtraPromotion => {
  return {
    ...promotion,
    date: {
      endDate: new Date(promotion.date.endDate).toISOString(),
      startDate: new Date(promotion.date.startDate).toISOString(),
    },
  };
};

const formatDateString = (date: string) => {
  if (date.charAt(date.length - 1) == 'Z') {
    return date;
  } else {
    return date.replace(/-/g, '/');
  }
};

export const checkExpiryDate = (
  startDate: string,
  endDate: string,
): boolean => {
  const today = new Date().getTime();
  startDate = formatDateString(startDate);
  endDate = formatDateString(endDate);
  try {
    const startTime = new Date(startDate).getTime();
    const endTime = new Date(endDate).getTime();
    if (today - startTime > 0 && endTime - today > 0) return true;
    return false;
  } catch (e) {
    return false;
  }
};

export const checkExpiredOrder = (predictCancelAt: Date | string) => {
  if (!predictCancelAt) return false;
  if (typeof predictCancelAt === 'string')
    return (
      new Date(
        /((\+\d{2}:\d{2})|Z)$/.test(predictCancelAt) // unknown timezone 2020-11-03T09:34:45 ???
          ? predictCancelAt
          : `${predictCancelAt}Z`,
      ) < new Date()
    );
  return predictCancelAt < new Date();
};

export const setCookieByName = (
  cname: string,
  cvalue: string,
  expireTime?: string,
  path?: string,
): void => {
  document.cookie = `${cname}=${cvalue}${
    expireTime ? `;expires=${expireTime}` : ''
  }${path ? `;path=${path}` : ''}`;
};

export const buildSlugUrl = (
  pageType: PageTypeEnum,
  name: string,
  id: string | number,
  isSellerCat = false,
) => {
  const trimmedId = id?.toString()?.trim();
  const trimmedName = name?.trim();
  if (!trimmedName || !trimmedName) return null;
  switch (pageType) {
    case PageTypeEnum.CATEGORY:
      if (isSellerCat) return `/${slug(trimmedName)}-scat.${trimmedId}`;
      return `/${slug(trimmedName)}-cat.${trimmedId}`;
    case PageTypeEnum.BRAND:
      return `/${slug(trimmedName)}-brand.${trimmedId}`;
    case PageTypeEnum.SELLER:
      return `/${slug(trimmedName)}-sel.${trimmedId}`;
    default:
      return null;
  }
};

export const isVnpay = (partner: string): boolean =>
  [
    'vietin',
    'vcb',
    'vnmart',
    'icheck',
    'agri',
    'bidv',
    'vp',
    'ivb',
    'exim',
    'abb',
    // do not change priority of sacombank pay and sacombankmb
    'sacombankmb',
    'sacombank',
    'saigonbank',
    'namabank',
    'bidc',
    'vietabank',
    'kienlongbank',
    'vnptpay',
  ].includes(partner);

export const isBankPartner = (partner: string): boolean =>
  [
    'vietin',
    'vcb',
    'vcb-loyalty',
    'vnmart',
    'icheck',
    'agri',
    'bidv',
    'bidv-vip',
    'vp',
    'tripi',
    'dinogo',
    'ivb',
    'exim',
    'abb',
    'sacombankmb',
    'sacombank',
    'wecare',
    'agent',
    'saigonbank',
    'namabank',
    'bidc',
    'vietabank',
    'coopbank',
    'kienlongbank',
    'vnptpay',
    'viviet',
    'hdbank',
    'tailoc68',
    'weluck',
    'baovietbank',
    'vietbank',
    'oceanbank',
    'publicbank',
    'mca',
    'vbsp',
    'scbmb',
  ].includes(partner);

export const isServerless = () => typeof window !== 'undefined';

/**
 *
 * @param hostname
 * @param env
 * @returns
 * convert [platform].localhost:3000 => [platform].[env].teko.vn
 */
export const getLocalDomain = (hostname: string, env = 'dev') => {
  if (/[^\.]*\.?localhost[^]*/g.test(hostname) === false) return hostname;
  return hostname.replace(
    /([^\.]*)\.?(localhost[^]*)/g,
    (_, platform = 'vnshop') => {
      return (platform || 'vnshop') + `.${env}.teko.vn`;
    },
  );
};

export const reloadPage = (url?: string) => {
  if (typeof window === 'undefined' || typeof window.location === 'undefined') {
    logger.error(
      'This function seems to be executed on server or not to be supported in the current browser',
    );

    return;
  }

  window.location.href =
    url ||
    window.location.origin + window.location.pathname + window.location.search;
};

export const clearCacheAndReloadPage = (url?: string) => {
  if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
    setCookieByName('nocache', 'true');
    url ? (window.location.href = url) : window.location.reload();
  }
};

export const getMobileAppToWebTerminalIdMapping = () => {
  try {
    const mapping = getEnv('MOBILE_APP_TO_WEB_TERMINAL_ID_MAPPING');
    return typeof mapping === 'string' ? JSON.parse(mapping) : null;
  } catch (e) {
    logger.error(e);
    return null;
  }
};

/**
 *
 * @param isDmsFlow
 * @description this function check and disable user select location action when user in Dms flow. Ref OMNI-1398
 * @returns Boolean
 */
export const isDisableUserSelectLocationAction = ({
  isDmsFlow,
}: {
  isDmsFlow?: boolean;
}) => {
  return isServerless() && isDmsFlow && !!getCookieByName(USER_TERMINAL_ID);
};

export const isUseUserLocationCode = ({
  isDmsFlow,
}: {
  isDmsFlow?: boolean;
}) => {
  return isServerless() && isDmsFlow && !!getCookieByName(USER_LOCATION_CODE);
};

export const isObjectEmpty = (obj: object) => {
  return !Object.keys(obj).length;
};

/**
 *
 * @param phoneNumber
 * @description this function convert phone number from '0...' to '+84...'
 * @returns string
 */
export const formatPhoneNumberPrefix = (phoneNumber: string) => {
  return phoneNumber.startsWith('0')
    ? '+84' + phoneNumber.slice(1)
    : phoneNumber;
};
